import React from "react";
import "./InvitKeyFacts.less";
import { Invit } from "../../../../types/invit.types";
import {
  toNumber,
  formatCurrency,
  findLatestValidValue,
  formatPerUnit,
} from "../../../../utils/financialHelper";

interface InvitKeyFactsProps {
  invit: Invit | null;
  title?: string;
}

const InvitKeyFacts: React.FC<InvitKeyFactsProps> = ({
  invit,
  title = "Key Facts",
}) => {
  const displayName = "cmp-invit-key-facts";
  if (!invit) {
    return (
      <section className={displayName}>
        <div className={`${displayName}__card`}>
          <p>Loading key facts...</p>
        </div>
      </section>
    );
  }

  // Corporate filing link mapping
  const corporateLinks: Record<string, string> = {
    "IRB InvIT Fund":
      "https://www.nseindia.com/companies-listing/corporate-filings-announcements?symbol=IRBINVIT&tabIndex=invitsreits",
    "Indus Infra Trust":
      "https://www.nseindia.com/companies-listing/corporate-filings-announcements?symbol=INDUSINVIT&tabIndex=invitsreits",
    "PowerGrid InvIT":
      "https://www.nseindia.com/companies-listing/corporate-filings-announcements?symbol=PGINVIT&tabIndex=invitsreits",
    "IndiGrid InvIT":
      "https://www.nseindia.com/companies-listing/corporate-filings-announcements?symbol=INDIGRID&tabIndex=invitsreits",
  };

  const corporateLink = invit?.name ? corporateLinks[invit.name] || "#" : "#";

  // check if InvIT is private
  const isPrivate = invit?.isPublic === false;

  // Get the latest financial data
  const getLatestFinancialData = () => {
    if (
      !invit ||
      !invit.financialMetrics ||
      invit.financialMetrics.length === 0
    ) {
      return null;
    }

    const latestYear = invit.financialMetrics[0];
    if (!latestYear.quarters || latestYear.quarters.length === 0) {
      return null;
    }

    const latestQuarter = latestYear.quarters[latestYear.quarters.length - 1];
    return latestQuarter;
  };

  // Count unique projects
  const getProjectCount = (): number => {
    if (
      !invit ||
      !invit.operationalMetrics ||
      invit.operationalMetrics.length === 0
    ) {
      return 0;
    }

    const uniqueProjects = new Set<string>();
    invit.operationalMetrics.forEach((yearData) => {
      if (yearData.semiAnnuals?.length) {
        yearData.semiAnnuals.forEach((semiAnnual) => {
          const projectName =
            semiAnnual.sectorOfOperation?.nameOfProject?.value;
          if (projectName && typeof projectName === "string") {
            uniqueProjects.add(projectName);
          }
        });
      }
    });

    return uniqueProjects.size;
  };

  // Get transformation capacity (only for PowerGrid)
  const getTransformationCapacity = (): string => {
    if (!invit?.operationalMetrics?.length) return "-";
    let totalCapacity = 0;

    invit.operationalMetrics.forEach((yearData) => {
      if (yearData.semiAnnuals?.length) {
        const latestSemi =
          yearData.semiAnnuals[yearData.semiAnnuals.length - 1];
        const sector = latestSemi.sectorOfOperation as any;
        const value = toNumber(sector?.capacity?.transformationCapacity?.value);
        if (value > 0) totalCapacity = value;
      }
    });

    return totalCapacity > 0
      ? `${totalCapacity.toLocaleString("en-IN")} MVA`
      : "-";
  };

  const latestFinancial = getLatestFinancialData();
  const aumValue = findLatestValidValue(
    invit.financialMetrics,
    "assetsUnderManagementOrEnterpriseValue"
  );

  const aum = aumValue ? formatCurrency(toNumber(aumValue)) : "-";

  const distributionYTDValue = findLatestValidValue(
    invit.financialMetrics,
    "distributionPerUnitYTD"
  );

  const distributionYTD = distributionYTDValue
    ? formatPerUnit(toNumber(distributionYTDValue))
    : "-";

  const ndcfValue = findLatestValidValue(
    invit.financialMetrics,
    "ndcfAtInvitYTD"
  );

  const ndcf = ndcfValue ? formatCurrency(toNumber(ndcfValue)) : "-";

  const projectCount = getProjectCount();
  const transformationCapacity = getTransformationCapacity();

  const facts: { value: string | number; label: string }[] = [];

  if (projectCount > 0)
    facts.push({ value: projectCount, label: "Number of Unique Projects" });
  if (aum !== "-") facts.push({ value: aum, label: "Assets Under Management" });
  if (transformationCapacity !== "-")
    facts.push({
      value: transformationCapacity,
      label: "Transformation Capacity",
    });
  if (distributionYTD !== "-")
    facts.push({
      value: distributionYTD,
      label: "Distribution per unit (YTD)",
    });
  if (ndcf !== "-")
    facts.push({ value: ndcf, label: "NDCF at InvIT (YTD) (INR Million)" });

  const productSummary =
    invit?.financialMetrics?.[0]?.introduction || "No introduction available.";

  const sponsor = "Sponsor information not available";

  return (
    <section className={displayName}>
      <div className={`${displayName}__card`}>
        <div className={`${displayName}__content`}>
          {/* LEFT */}
          <div className={`${displayName}__left`}>
            <h1 className={`${displayName}__title`}>{title}</h1>

            {facts.length > 0 ? (
              <div className={`${displayName}__list`}>
                {facts.map((fact, idx) => (
                  <div
                    className={`${displayName}__item ${
                      idx === facts.length - 1
                        ? `${displayName}__item--last`
                        : ""
                    }`}
                    key={idx}
                  >
                    <div className={`${displayName}__value`}>{fact.value}</div>
                    <p className={`${displayName}__label`}>{fact.label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p>No key facts available</p>
            )}
          </div>

          {/* RIGHT */}
          <div className={`${displayName}__right`}>
            <h4 className={`${displayName}__heading`}>Introduction</h4>
            <p className={`${displayName}__text`}>
              {productSummary.split("\n").map((line, idx) => (
                <span key={idx}>
                  {line}
                  <br />
                </span>
              ))}
            </p>

            {/* <h3 className={`${displayName}__heading`}>
              Sponsor & Sponsor group
            </h3>
            <p className={`${displayName}__text`}>{sponsor}</p> */}

            {!isPrivate && (
              <>
                <h3 className={`${displayName}__heading`}>
                  For Corporate Filing information
                </h3>
                <p className={`${displayName}__text`}>
                  Click{" "}
                  <a
                    href={corporateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${displayName}__corporate-link`}
                  >
                    here
                  </a>
                </p>
              </>
            )}
          </div>
        </div>

        {/* Bottom note */}
        <p className={`${displayName}__unit-note`}>
          All figures on this page are in ₹ Mn.
        </p>
      </div>
    </section>
  );
};

export default InvitKeyFacts;
